import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  addBrandBookRule,
  type BrandBookRule,
  deleteBrandBookRule,
  fetchBrandBookRules,
  MAX_RULE_LENGTH,
  RULE_AREAS,
  type RuleArea,
  updateBrandBookRule,
} from './api.ts';
import { describeError, redirectIfUnauthorised, useAppData } from './data.tsx';
import { Icon } from './Icon.tsx';
import { Link } from './router.tsx';
import { localTime } from './schedule.ts';
import { useToast } from './toast.tsx';
import { Badge, Notice, Panel, Skeleton } from './ui.tsx';

/**
 * House rules (one-shot ads US-013): what the system learned from the
 * operator's revision feedback, kept on the brand book so every next content
 * of it starts with them.
 *
 * Nothing here is approved: a learned rule applies the moment it is saved.
 * The operator stays in control by rewording or deleting what is wrong, and
 * by adding what was never said.
 */

export const RULE_AREA_LABEL: Record<RuleArea, string> = {
  look: 'Look',
  'end-card': 'End card',
  logo: 'Logo',
  voice: 'Voice',
  sound: 'Sound',
  pace: 'Pace',
  words: 'Words',
  staging: 'Staging',
  other: 'Other',
};

interface RuleDraft {
  readonly rule: string;
  readonly area: RuleArea;
}

export function HouseRulesPanel({ brandBookId, name }: { readonly brandBookId: string; readonly name: string }) {
  const toast = useToast();
  const { contents } = useAppData();
  const [rules, setRules] = useState<BrandBookRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<RuleDraft | null>(null);
  const [editing, setEditing] = useState<(RuleDraft & { readonly id: string }) | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchBrandBookRules(brandBookId, controller.signal)
      .then((found) => {
        setRules(found);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  const run = (key: string, work: () => Promise<void>): void => {
    setBusy(key);
    work()
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const onAdd = (event: FormEvent): void => {
    event.preventDefault();
    if (adding === null || adding.rule.trim() === '') return;
    const draft = adding;
    run('add', async () => {
      const added = await addBrandBookRule(brandBookId, draft.rule.trim(), draft.area);
      setRules((previous) => [...(previous ?? []), added]);
      setAdding(null);
      toast.ok(`Added a house rule to ${name}.`);
    });
  };

  const onSave = (event: FormEvent): void => {
    event.preventDefault();
    if (editing === null || editing.rule.trim() === '') return;
    const draft = editing;
    run(`edit-${draft.id}`, async () => {
      const saved = await updateBrandBookRule(brandBookId, draft.id, { rule: draft.rule.trim(), area: draft.area });
      setRules((previous) => (previous ?? []).map((rule) => (rule.id === saved.id ? saved : rule)));
      setEditing(null);
      toast.ok('Saved the house rule.');
    });
  };

  const onDelete = (rule: BrandBookRule): void => {
    run(`delete-${rule.id}`, async () => {
      await deleteBrandBookRule(brandBookId, rule.id);
      setRules((previous) => (previous ?? []).filter((candidate) => candidate.id !== rule.id));
      if (editing?.id === rule.id) setEditing(null);
      toast.ok('Deleted the house rule; no next content starts with it.');
    });
  };

  const contentName = (id: string): string => contents?.find((content) => content.id === id)?.name ?? id;
  const groups = RULE_AREAS.map((area) => ({
    area,
    rules: (rules ?? []).filter((rule) => rule.area === area),
  })).filter((group) => group.rules.length > 0);

  return (
    <Panel
      title="House rules"
      icon="tasklist"
      id={`house-rules-${brandBookId}`}
      meta={
        rules === null ? null : rules.length === 0 ? (
          <Badge tone="neutral">none yet</Badge>
        ) : (
          <Badge tone="done">{rules.length === 1 ? '1 rule' : `${String(rules.length)} rules`}</Badge>
        )
      }
      actions={
        <button
          type="button"
          className="button button--small"
          disabled={rules === null || adding !== null}
          onClick={() => {
            setAdding({ rule: '', area: 'look' });
          }}
        >
          <Icon name="plus" />
          Add rule
        </button>
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {rules === null ? (
        error === null && <Skeleton lines={3} />
      ) : (
        <div className="stack">
          <p className="muted">
            Learned from the changes asked on the contents of {name}, and read by the planner, compose and the
            critics of every next content. Only the ad kit outranks them. Reword or delete what is wrong.
          </p>
          {adding !== null && (
            <RuleForm
              draft={adding}
              submitLabel="Add rule"
              busy={busy === 'add'}
              onChange={setAdding}
              onSubmit={onAdd}
              onCancel={() => {
                setAdding(null);
              }}
            />
          )}
          {groups.length === 0 && adding === null && (
            <p className="muted">
              No house rules yet. They are learned from the feedback given with <strong>Request changes</strong> and from corrections typed while planning, or added here.
            </p>
          )}
          {groups.map((group) => (
            <div className="field" key={group.area}>
              <span className="field__label">{RULE_AREA_LABEL[group.area]}</span>
              <ul className="rows rows--divided">
                {group.rules.map((rule) =>
                  editing?.id === rule.id ? (
                    <li className="row" key={rule.id}>
                      <RuleForm
                        draft={editing}
                        submitLabel="Save rule"
                        busy={busy === `edit-${rule.id}`}
                        onChange={(draft) => {
                          setEditing({ ...draft, id: rule.id });
                        }}
                        onSubmit={onSave}
                        onCancel={() => {
                          setEditing(null);
                        }}
                      />
                    </li>
                  ) : (
                    <li className="row" key={rule.id}>
                      <div className="row__main">
                        <span className="row__title">{rule.rule}</span>
                        <span className="row__meta">
                          <RuleOrigin rule={rule} contentName={contentName} /> · {localTime(rule.updatedAt)}
                        </span>
                      </div>
                      <div className="row__actions">
                        <button
                          type="button"
                          className="button button--small button--quiet"
                          disabled={busy !== null}
                          onClick={() => {
                            setEditing({ id: rule.id, rule: rule.rule, area: rule.area });
                          }}
                        >
                          <Icon name="pencil" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button button--small button--quiet button--danger"
                          disabled={busy !== null}
                          onClick={() => {
                            onDelete(rule);
                          }}
                        >
                          <Icon name={busy === `delete-${rule.id}` ? 'sync' : 'trash'} />
                          Delete
                        </button>
                      </div>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/** "learned from <content> v<n>", linking to the content; or that it was typed on the book. */
function RuleOrigin({
  rule,
  contentName,
}: {
  readonly rule: BrandBookRule;
  readonly contentName: (id: string) => string;
}) {
  if (rule.sourceContentId === null) return <>added on the brand book</>;
  return (
    <>
      {'learned from '}
      <Link className="link" href={`/content/${encodeURIComponent(rule.sourceContentId)}`}>
        {contentName(rule.sourceContentId)}
        {rule.sourceVersion !== null && ` v${String(rule.sourceVersion)}`}
      </Link>
    </>
  );
}

function RuleForm({
  draft,
  submitLabel,
  busy,
  onChange,
  onSubmit,
  onCancel,
}: {
  readonly draft: RuleDraft;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly onChange: (draft: RuleDraft) => void;
  readonly onSubmit: (event: FormEvent) => void;
  readonly onCancel: () => void;
}) {
  const length = draft.rule.trim().length;
  return (
    <form className="form form--tight" onSubmit={onSubmit}>
      <div className="field__pair">
        <input
          className="field__input"
          aria-label="Rule"
          value={draft.rule}
          maxLength={MAX_RULE_LENGTH}
          placeholder="One sentence, e.g. The logo appears only on the end card."
          onChange={(event) => {
            onChange({ ...draft, rule: event.target.value });
          }}
        />
        <select
          className="field__input field__input--narrow"
          aria-label="Area"
          value={draft.area}
          onChange={(event) => {
            onChange({ ...draft, area: event.target.value as RuleArea });
          }}
        >
          {RULE_AREAS.map((area) => (
            <option key={area} value={area}>
              {RULE_AREA_LABEL[area]}
            </option>
          ))}
        </select>
      </div>
      <div className="field__actions">
        <button type="submit" className="button button--primary button--small" disabled={busy || length === 0}>
          <Icon name={busy ? 'sync' : 'check'} />
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className="button button--small button--quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The house rules one content's revisions taught (one-shot ads US-013):
 * every live rule of its brand book whose source is this content, keyed by
 * the version the feedback was given on — and those its planning messages
 * taught, which have no version (the ad bible US-026). Read again whenever
 * `refreshKey` changes, because the rules are learned in the background
 * after **Request changes** and after each planning message.
 */
export function useTaughtRules(
  brandBookId: string | null,
  contentId: string,
  refreshKey: string,
): {
  readonly byVersion: (version: number) => readonly BrandBookRule[];
  readonly fromPlanning: readonly BrandBookRule[];
  readonly remove: (rule: BrandBookRule) => void;
  readonly busy: string | null;
} {
  const toast = useToast();
  const [rules, setRules] = useState<BrandBookRule[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (brandBookId === null) {
      setRules([]);
      return;
    }
    const controller = new AbortController();
    fetchBrandBookRules(brandBookId, controller.signal)
      .then((found) => {
        setRules(found.filter((rule) => rule.sourceContentId === contentId));
      })
      .catch((cause: unknown) => {
        // The list is a footnote of the revision history; without it the page still works.
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
      });
    return () => controller.abort();
  }, [brandBookId, contentId, refreshKey]);

  const byVersion = useCallback(
    (version: number): readonly BrandBookRule[] => rules.filter((rule) => rule.sourceVersion === version),
    [rules],
  );

  const fromPlanning = useMemo(() => rules.filter((rule) => rule.sourceVersion === null), [rules]);

  const remove = (rule: BrandBookRule): void => {
    setBusy(rule.id);
    deleteBrandBookRule(rule.brandBookId, rule.id)
      .then(() => {
        setRules((previous) => previous.filter((candidate) => candidate.id !== rule.id));
        toast.ok('Deleted the house rule; no next content starts with it.');
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  return { byVersion, fromPlanning, remove, busy };
}

/** "Saved as house rules: …" under one revision, with a delete link each. */
export function SavedAsHouseRules({
  rules,
  busy,
  onDelete,
  title = 'Saved as house rules:',
}: {
  readonly rules: readonly BrandBookRule[];
  readonly busy: string | null;
  readonly onDelete: (rule: BrandBookRule) => void;
  readonly title?: string;
}) {
  if (rules.length === 0) return null;
  return (
    <div className="review__taught">
      <span className="review__taught-title">{title}</span>
      <ul className="review__taught-list">
        {rules.map((rule) => (
          <li key={rule.id}>
            <Badge tone="neutral">{RULE_AREA_LABEL[rule.area]}</Badge> {rule.rule}{' '}
            <button
              type="button"
              className="link link--button review__taught-delete"
              disabled={busy !== null}
              onClick={() => {
                onDelete(rule);
              }}
            >
              {busy === rule.id ? 'deleting…' : 'delete'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
